// components/i18n/LocalDate.tsx
import React from 'react';
import { useCountry } from '@/contexts/CountryContext';

interface LocalDateProps {
  date: Date | string;
  showTime?: boolean;
  className?: string;
  format?: 'short' | 'long' | 'medium';
}

/**
 * LocalDate component that automatically formats dates based on the current country context
 * Handles DD/MM/YYYY for UK and MM/DD/YYYY for US
 */
export const LocalDate: React.FC<LocalDateProps> = ({ 
  date, 
  showTime = false, 
  className = '',
  format = 'short'
}) => {
  const { formatDate, currentCountryCode } = useCountry();

  if (!date) {
    return <span className={className}>—</span>;
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Handle invalid dates
  if (isNaN(dateObj.getTime())) {
    return <span className={className}>Invalid Date</span>;
  }

  let formattedDate: string;

  if (format === 'long') {
    // Full date format (e.g., "Monday, 1st January 2024")
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    formattedDate = dateObj.toLocaleDateString(
      currentCountryCode === 'US' ? 'en-US' : 'en-GB',
      options
    );
  } else if (format === 'medium') {
    // Medium date format (e.g., "1 Jan 2024")
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };
    formattedDate = dateObj.toLocaleDateString(
      currentCountryCode === 'US' ? 'en-US' : 'en-GB',
      options
    );
  } else {
    // Short date format using country context
    formattedDate = formatDate(dateObj);
  }

  // Add time if requested
  if (showTime) {
    const timeString = dateObj.toLocaleTimeString(
      currentCountryCode === 'US' ? 'en-US' : 'en-GB',
      { hour: '2-digit', minute: '2-digit' }
    );
    formattedDate += ` ${timeString}`;
  }

  return (
    <span className={className} title={dateObj.toISOString()}>
      {formattedDate}
    </span>
  );
};

interface LocalDateInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  min?: string;
  max?: string;
}

/**
 * LocalDateInput component that provides date input with appropriate format hints
 */
export const LocalDateInput: React.FC<LocalDateInputProps> = ({
  value,
  onChange,
  placeholder,
  className = '',
  disabled = false,
  min,
  max,
}) => {
  const { getDateFormat } = useCountry();
  const dateFormat = getDateFormat();

  const defaultPlaceholder = placeholder || dateFormat.toLowerCase();

  return (
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={defaultPlaceholder}
      className={`px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
      disabled={disabled}
      min={min}
      max={max}
    />
  );
};

/**
 * Utility function to convert date string from one format to another
 */
export const convertDateFormat = (
  dateString: string, 
  fromFormat: 'DD/MM/YYYY' | 'MM/DD/YYYY', 
  toFormat: 'DD/MM/YYYY' | 'MM/DD/YYYY'
): string => {
  if (!dateString || fromFormat === toFormat) return dateString;

  const parts = dateString.split('/');
  if (parts.length !== 3) return dateString;

  if (fromFormat === 'DD/MM/YYYY' && toFormat === 'MM/DD/YYYY') {
    // Convert DD/MM/YYYY to MM/DD/YYYY
    return `${parts[1]}/${parts[0]}/${parts[2]}`;
  } else if (fromFormat === 'MM/DD/YYYY' && toFormat === 'DD/MM/YYYY') {
    // Convert MM/DD/YYYY to DD/MM/YYYY
    return `${parts[1]}/${parts[0]}/${parts[2]}`;
  }

  return dateString;
};

export default LocalDate; 